import { Box } from '@mui/material';
import { useEffect, useRef } from 'react';

const HERE_API_KEY = process.env.REACT_APP_PUBLIC_HERE_API_KEY;

interface BrandGeoLocationMapProps {
    lat: number;
    lng: number;
}

const BrandGeoLocationMap = ({ lat, lng }: BrandGeoLocationMapProps) => {
    const mapRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!mapRef.current || !HERE_API_KEY) return;

        let H: any;
        try {
            const mod = require('@here/maps-api-for-javascript');
            H = mod.default || mod;
        } catch {
            return;
        }

        let map: any;
        let isDisposed = false;
        let resizeObserver: ResizeObserver;
        const container = mapRef.current;

        const rafId = requestAnimationFrame(() => {
            if (!container) return;

            const platform = new H.service.Platform({ apikey: HERE_API_KEY });
            const defaultLayers = platform.createDefaultLayers();

            const mapLayer = defaultLayers?.vector?.normal?.map ?? defaultLayers?.raster?.normal?.map;
            if (!mapLayer) return;

            map = new H.Map(container, mapLayer, {
                zoom: 14,
                center: { lat, lng },
            });

            H.ui.UI.createDefault(map, defaultLayers);

            const marker = new H.map.Marker({ lat, lng });
            map.addObject(marker);

            resizeObserver = new ResizeObserver(() => {
                if (isDisposed || !map) return;
                const viewPort = map.getViewPort?.();
                if (!viewPort?.element) return;
                viewPort.resize();
            });
            resizeObserver.observe(container);
        });

        return () => {
            isDisposed = true;
            cancelAnimationFrame(rafId);
            resizeObserver?.disconnect();
            map?.dispose();
        };
    }, [lat, lng]);

    return (
        <Box
            ref={mapRef}
            sx={{
                width: '100%',
                height: 220,
                borderRadius: 1,
                overflow: 'hidden',
                border: '1px solid',
                borderColor: 'divider',
            }}
        />
    );
};

export default BrandGeoLocationMap;
